import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Maximize, Minimize } from "lucide-react";
import { pointApi, presentationApi } from "../../services/presentationApi";
import ScaledSlide from "../Offers/proposal/ScaledSlide";
import SlideCanvas from "../Offers/proposal/SlideCanvas";

const NEXT = ["ArrowRight", "ArrowDown", "PageDown", " ", "Enter"];
const PREVIOUS = ["ArrowLeft", "ArrowUp", "PageUp", "Backspace"];
const IDLE_MS = 2500;

// Fenêtre de présentation, ouverte à part depuis l'éditeur : les slides seules sur fond noir, sans rien de Deepflow
// autour (ni menu, ni notifications, ni Nexus). C'est elle qu'on partage en visio (« partager une fenêtre ») ; les
// jumeaux s'y manipulent. Flèches, espace ou boutons pour avancer, F pour le plein écran ; le contenu est relu quand
// la fenêtre revient au premier plan (retouches faites dans l'éditeur entre-temps).
// Un point client (kind="point") se présente pareil : ses slides assemblées, chacune avec le pied de page de sa
// présentation d'origine.
const loadPoint = (id) => pointApi.get(id).then((point) => ({
  presentation: { title: point.assembly.title },
  content: { meta: point.deck.meta, slides: point.deck.slides },
  embeds: point.deck.embeds,
}));

const PresenterWindow = ({ kind = "presentation" }) => {
  const { proposalId } = useParams();
  const id = Number(proposalId);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [index, setIndex] = useState(0);
  const [controls, setControls] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const currentId = useRef(null);
  const idle = useRef(null);

  const load = useCallback(() => {
    (kind === "point" ? loadPoint(id) : presentationApi.get(id)).then(setData).catch((err) => setError(err.message));
  }, [id, kind]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [load]);

  const slides = (data?.content?.slides || []).filter((slide) => !slide.hidden);
  const pages = Object.fromEntries(slides.map((slide, position) => [slide.id, position + 1]));
  // Après une relecture, on reste sur la même slide même si l'ordre a changé.
  useEffect(() => {
    if (!slides.length) return;
    const found = slides.findIndex((slide) => slide.id === currentId.current);
    setIndex((value) => (found >= 0 ? found : Math.min(value, slides.length - 1)));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const slide = slides[Math.min(index, Math.max(slides.length - 1, 0))];
  currentId.current = slide?.id || null;

  useEffect(() => {
    if (data) document.title = data.presentation.title;
  }, [data]);

  const go = useCallback((delta) => setIndex((value) => Math.max(0, Math.min(slides.length - 1, value + delta))),
    [slides.length]);
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.()?.catch?.(() => {});
    else document.documentElement.requestFullscreen?.()?.catch?.(() => {});
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (NEXT.includes(event.key)) go(1);
      else if (PREVIOUS.includes(event.key)) go(-1);
      else if (event.key === "Home") setIndex(0);
      else if (event.key === "End") setIndex(slides.length - 1);
      else if (event.key === "f" || event.key === "F") toggleFullscreen();
      else return;
      event.preventDefault();
    };
    const onFullscreen = () => setFullscreen(Boolean(document.fullscreenElement));
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFullscreen);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFullscreen);
    };
  }, [go, slides.length, toggleFullscreen]);

  // Boutons visibles quand la souris bouge, effacés ensuite : rien ne traîne sur l'écran partagé.
  const wake = () => {
    setControls(true);
    clearTimeout(idle.current);
    idle.current = setTimeout(() => setControls(false), IDLE_MS);
  };
  useEffect(() => {
    idle.current = setTimeout(() => setControls(false), IDLE_MS);
    return () => clearTimeout(idle.current);
  }, []);

  if (error) return <div className="flex h-screen items-center justify-center bg-black text-sm text-slate-300">{error}</div>;
  if (!data) return <div className="h-screen bg-black" />;
  if (!slide) return <div className="flex h-screen items-center justify-center bg-black text-sm text-slate-300">Aucune slide visible.</div>;

  const button = "rounded-full bg-white/10 p-2 text-white hover:bg-white/20 disabled:opacity-30";
  return (
    <div className="relative h-screen w-screen overflow-hidden bg-black" onMouseMove={wake}
      style={{ cursor: controls ? "default" : "none" }}>
      <div className="h-full w-full p-2">
        <ScaledSlide fit center>
          <SlideCanvas slide={slide} meta={data.content.meta} page={pages[slide.id]} embeds={data.embeds || {}} live
            deckTitle={data.presentation.title} />
        </ScaledSlide>
      </div>
      <div className={`absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/70 px-3 py-1.5 text-sm text-white transition-opacity ${
        controls ? "opacity-100" : "pointer-events-none opacity-0"}`}>
        <button type="button" className={button} onClick={() => go(-1)} disabled={index === 0} aria-label="Slide précédente">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="min-w-[4rem] text-center tabular-nums">{index + 1} / {slides.length}</span>
        <button type="button" className={button} onClick={() => go(1)} disabled={index >= slides.length - 1} aria-label="Slide suivante">
          <ChevronRight className="h-5 w-5" />
        </button>
        <button type="button" className={button} onClick={toggleFullscreen} aria-label={fullscreen ? "Quitter le plein écran" : "Plein écran"}
          title="Plein écran (F)">
          {fullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

export default PresenterWindow;
